import { blueprintBg } from "@/theme/styleHelpers";
import CloseIcon from "@mui/icons-material/CloseRounded";
/**
 * KeyboardShortcutsDialog — press `?` anywhere to open the read-only
 * shortcuts cheat-sheet. Mirrors the CommandPalette's chassis (mono
 * input row → blueprint body) but inverts the semantic: that one
 * *does* things, this one *teaches*.
 *
 * Mounted globally at AppLayout root alongside the CommandPalette.
 * Listening for `?` (Shift + /) is gated when the user is typing in
 * an input — same etiquette as Cmd+K.
 *
 * Sections are static for now (Global / Chat / Drafts / Canvas).
 * Adding a new keybinding = one row in the SECTIONS array.
 */
import KeyboardIcon from "@mui/icons-material/KeyboardRounded";
import {
  Backdrop,
  Box,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useEffect } from "react";
import { create } from "zustand";

interface ShortcutsState {
  isOpen: boolean;
}
interface ShortcutsActions {
  openShortcuts: () => void;
  closeShortcuts: () => void;
  toggleShortcuts: () => void;
}

export const useShortcutsDialog = create<ShortcutsState & ShortcutsActions>()(
  (set) => ({
    isOpen: false,
    openShortcuts: () => set({ isOpen: true }),
    closeShortcuts: () => set({ isOpen: false }),
    toggleShortcuts: () => set((s) => ({ isOpen: !s.isOpen })),
  }),
);

interface Shortcut {
  /** Visual key sequence — array of strings rendered as kbd-style chips. */
  keys: string[];
  caption: string;
}

interface Section {
  title: string;
  shortcuts: Shortcut[];
}

const SECTIONS: Section[] = [
  {
    title: "Global",
    shortcuts: [
      { keys: ["⌘", "K"], caption: "Open command palette" },
      { keys: ["?"], caption: "Show this dialog" },
      { keys: ["esc"], caption: "Close any open modal" },
    ],
  },
  {
    title: "Chat",
    shortcuts: [
      { keys: ["↵"], caption: "Send message" },
      { keys: ["⇧", "↵"], caption: "New line in composer" },
    ],
  },
  {
    title: "Drafts panel",
    shortcuts: [
      { keys: ["←", "→"], caption: "Step through op-timeline snapshots" },
    ],
  },
  {
    title: "Identity review (rename theatre)",
    shortcuts: [
      { keys: ["Y"], caption: "Confirm suggested rename" },
      { keys: ["N"], caption: "Reject suggested rename" },
      { keys: ["K"], caption: "Skip — decide later" },
      { keys: ["←", "→"], caption: "Previous / next suggestion" },
    ],
  },
];

export function KeyboardShortcutsDialog() {
  const isOpen = useShortcutsDialog((s) => s.isOpen);
  const closeShortcuts = useShortcutsDialog((s) => s.closeShortcuts);

  if (!isOpen) return null;

  return (
    <Backdrop
      open={isOpen}
      onClick={closeShortcuts}
      sx={{
        zIndex: (t) => t.zIndex.modal,
        backgroundColor: "rgba(7, 13, 24, 0.62)",
        backdropFilter: "blur(4px)",
        alignItems: "flex-start",
        pt: "12vh",
      }}
    >
      <Box
        role="dialog"
        aria-label="Keyboard shortcuts"
        onClick={(e) => e.stopPropagation()}
        sx={(t) => ({
          width: "min(620px, 92vw)",
          maxHeight: "min(72vh, 560px)",
          display: "flex",
          flexDirection: "column",
          bgcolor: t.palette.surface.default,
          border: `1px solid ${t.palette.border.default}`,
          borderRadius: 2,
          animation: `cf-shortcuts-in 220ms cubic-bezier(0.4, 0, 0.2, 1)`,
          boxShadow: `
            0 24px 48px -16px rgba(0, 0, 0, 0.55),
            0 0 0 1px ${alpha(t.palette.accent.main, 0.18)}
          `,
          "@keyframes cf-shortcuts-in": {
            from: {
              opacity: 0,
              transform: "translateY(-6px) scale(0.98)",
            },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        })}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={(t) => ({
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${t.palette.border.default}`,
          })}
        >
          <KeyboardIcon
            fontSize="small"
            sx={{ color: "primary.main", fontSize: 18 }}
          />
          <Typography
            variant="subtitle2"
            sx={(t) => ({
              flex: 1,
              fontFamily: t.fonts.mono,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: t.palette.text.primary,
            })}
          >
            Keyboard shortcuts
          </Typography>
          <IconButton size="small" onClick={closeShortcuts}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Body */}
        <Box
          sx={(t) => ({
            ...blueprintBg(t),
            flex: 1,
            overflowY: "auto",
            py: 1,
          })}
        >
          {SECTIONS.map((section) => (
            <Box key={section.title} sx={{ py: 1.25 }}>
              <Typography
                variant="overline"
                sx={(t) => ({
                  display: "block",
                  px: 2,
                  pb: 0.5,
                  color: t.palette.editorial.main,
                  fontSize: 10,
                  letterSpacing: "0.16em",
                })}
              >
                {section.title}
              </Typography>
              <Stack divider={null}>
                {section.shortcuts.map((s) => (
                  <Stack
                    // Caption is unique within a section; keys never reorder
                    // because the section catalog is static.
                    key={s.caption}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={(t) => ({
                      px: 2,
                      py: 0.875,
                      "&:hover": {
                        bgcolor: alpha(t.palette.accent.main, 0.06),
                      },
                    })}
                  >
                    <Typography
                      variant="body2"
                      sx={(t) => ({
                        flex: 1,
                        color: t.palette.text.primary,
                        fontSize: 13,
                      })}
                    >
                      {s.caption}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {s.keys.map((k, ki) => (
                        <Box
                          key={ki}
                          sx={(t) => ({
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 22,
                            height: 22,
                            px: 0.625,
                            borderRadius: 0.75,
                            border: `1px solid ${t.palette.border.default}`,
                            bgcolor: t.palette.surface.raised,
                            color: t.palette.text.primary,
                            fontFamily: t.fonts.mono,
                            fontSize: 11,
                            fontWeight: 500,
                          })}
                        >
                          {k}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box
          sx={(t) => ({
            px: 2,
            py: 1,
            borderTop: `1px solid ${t.palette.border.default}`,
            color: t.palette.text.disabled,
            fontFamily: t.fonts.mono,
            fontSize: 10,
            letterSpacing: "0.04em",
          })}
        >
          Press{" "}
          <Box
            component="span"
            sx={(t) => ({
              display: "inline-flex",
              alignItems: "center",
              px: 0.5,
              py: 0,
              borderRadius: 0.5,
              border: `1px solid ${t.palette.border.default}`,
              color: t.palette.text.secondary,
              fontFamily: t.fonts.mono,
              fontSize: 10,
              mx: 0.4,
            })}
          >
            ?
          </Box>
          again to close · Cmd+K for actions
        </Box>
      </Box>
    </Backdrop>
  );
}

/**
 * Global hotkey listener — `?` (Shift+/) toggles the dialog. Skipped
 * when the user is typing in an input / textarea / contenteditable so
 * the shortcut never swallows a literal "?" character mid-prompt.
 */
export function GlobalShortcutsHotkey() {
  const toggle = useShortcutsDialog((s) => s.toggleShortcuts);
  const close = useShortcutsDialog((s) => s.closeShortcuts);
  const isOpen = useShortcutsDialog((s) => s.isOpen);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      // `?` is Shift + / on US keyboards. The robust check is the
      // produced `key` value, which is "?" when Shift is held.
      if (e.key === "?") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        close();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, close, isOpen]);

  return null;
}
